import type { Fetcher } from '@atlassian/post-office-placement-data/types';

import { type InitialDataStates, useData } from '@post-office/placement-contexts/hooks';

import { type SerializableRecord } from '@post-office/serializable-record';

import type { InAppPlacements } from '@post-office/shared-contracts/types';

import { useCallback } from 'react';

import { fetchPlacementDataAdminHub } from '../utils/fetch-placement-data';

const FIRST = 0;

const SECOND = 1;

const REQUEST_URL = `/gateway/api/post-office/api/v1/placements/`;

const isNotEmpty = (input: unknown) => !(typeof input === 'undefined' || input === null);

export const stringifyObjectValues = <T extends Record<string, unknown>>(
	input: T,
): Record<string, string> => {
	const entries = Object.entries(input);
	const stringifiedEntries = entries
		.filter(([, value]) => isNotEmpty(value))
		.map(([key, value]) => [key, String(value)]);
	return Object.fromEntries(stringifiedEntries) as Record<string, string>;
};

export const buildUrlWithContext = (
	path: string,
	context: SerializableRecord,
	placementId: string,
): string => {
	const normalizedPath = `/${path.charAt(FIRST) === '/' ? path.substring(SECOND) : path}`;
	const contextAsSearchParams = new URLSearchParams(stringifyObjectValues(context)).toString();
	const formattedSearchParams = contextAsSearchParams
		? `?${contextAsSearchParams}`
		: contextAsSearchParams;
	return encodeURI(`${REQUEST_URL}${placementId}${normalizedPath}${formattedSearchParams}`);
};

// TODO use "createUsePlacement" from "@post-office/placement-common" instead
export const createUsePlacement = (
	placementId: InAppPlacements,
	fetcher: Fetcher = { fetch: globalThis.fetch },
) => {
	if (!fetcher) {
		throw new Error(
			'No fetcher found. If you are using usePlacement outside or a browser you must supply a fetcher yourself',
		);
	}

	return <D extends SerializableRecord, C extends SerializableRecord>(
		path: string,
		context: C,
		requestOptions?: RequestInit,
	): InitialDataStates<D> => {
		/**
		 * As part of Read/Unread feature, the /messages API needs to be called with an additional context attribute.
		 * Adding the context as part of the dependency array was calling this API too many times. To fix this,
		 * the url is now built with the context so that is can be memoized on the entire path (including context)
		 */
		const finalPath = buildUrlWithContext(path, context, placementId);
		const promiseToResolve = useCallback(
			() => fetchPlacementDataAdminHub(finalPath, requestOptions, fetcher.fetch),
			[finalPath],
		);

		return useData<D>(promiseToResolve, {
			analytics: {
				name: placementId,
				path: path,
			},
		});
	};
};
